import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  /**
   * Handles a cart lines update - waits for the add to land and the section to morph
   * before opening the drawer. Only acts on an add when the drawer needs to open. If
   * the drawer is already open, CartItemsComponent handles the update.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = async (event) => {
    if (!this.hasAttribute('auto-open') || event.action !== 'add') return;

    // If dialog is already open, don't interfere - CartItemsComponent handles updates
    // This prevents double-morph issues when removing/updating items in an open drawer
    if (this.refs.dialog.open) return;

    /** @type {{ detail?: { sections?: Record<string, string>, didError?: boolean } }} */
    let resolved = {};
    try {
      resolved = (await event.promise) ?? {};
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      return;
    }

    if (resolved.detail?.didError) return;

    // Get the cart-items-component inside this drawer to find its section ID
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent?.dataset?.sectionId;

    if (sectionId) {
      try {
        // Check if section HTML was included in the event
        const sectionHtml = resolved.detail?.sections?.[sectionId];

        if (sectionHtml) {
          // Section HTML was pre-fetched, morph it before opening
          morphSection(sectionId, sectionHtml);
        } else {
          // Section not included, need to fetch it
          await sectionRenderer.renderSection(sectionId, { cache: false });
        }
      } catch (error) {
        console.error('Failed to update cart drawer content:', error);
        // Still open the drawer even if morph failed - better UX than doing nothing
      }
    }

    // Open the drawer after content is ready (or even if update failed)
    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
